import PropTypes from 'prop-types'
import { Modal, Button, Table, Tag, Tooltip } from 'antd'
import { ExportOutlined } from '@ant-design/icons'
import { useCallback, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import dayjs from 'dayjs'
import api from '../../api'
import { useAsync } from '../../hooks'

const useStyles = createUseStyles({
  miniBlock: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    marginRight: '.5em',
    borderRadius: '4px',
    backgroundColor: '#1890ff',
    color: '#1890ff',
  },
  hCenter: {
    textAlign: 'center',
  },
})

export default function TransmissionsModal({ visible, patientId, onCancel }) {
  const [transmissionsCount, setTransmissionsCount] = useState(0)
  const [transmissions, setTransmissions] = useState([])
  const [conditions, setConditions] = useState({ page: 1, perPage: 8 })
  const classes = useStyles()

  const isValidDate = useCallback(dateString => {
    if (!dateString || typeof dateString !== 'string' || !dateString.trim()) {
      return false
    }
    const parsed = dayjs(dateString)
    if (!parsed.isValid()) {
      return false
    }
    return true
  }, [])

  const formatDate = useCallback(
    dateString => {
      if (!isValidDate(dateString)) return 'Manually Uploaded'
      return dayjs(dateString).format('MMM Do YYYY')
    },
    [isValidDate],
  )

  const getTransmissions = useCallback(
    async () =>
      api.patients.getTransmissions(
        patientId,
        conditions.perPage,
        conditions.page,
      ),
    [patientId, conditions],
  )

  const { pending, value: transmissionsResponse } = useAsync(getTransmissions)

  useEffect(() => {
    const total = transmissionsResponse?.headers?.get('X-Total') || 0
    const response = transmissionsResponse?.body || []

    setTransmissions(response)
    setTransmissionsCount(parseInt(total, 10))
  }, [transmissionsResponse])

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const columns = [
    {
      title: 'Transmission Date',
      key: 'transmissionDate',
      dataIndex: 'transmissionDate',
      render(_text, item) {
        return (
          <>
            <span className={classes.miniBlock} />
            <span className={classes.entryLink}>
              {formatDate(item.transmissionDate)}
            </span>
          </>
        )
      },
    },
    {
      title: 'Device',
      dataIndex: 'device.model',
      render(text) {
        return <Tag color="blue">{text}</Tag>
      },
    },
    {
      title: 'View',
      ket: 'view',
      render(_text, item) {
        return (
          <Tooltip title="Open in a new tab" placement="top">
            <Link to={`/transmissions/${item.id}`} target="_blank">
              <Button type="link" icon={<ExportOutlined />} />
            </Link>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <Modal
      title="Patient's Transmissions"
      open={visible}
      onCancel={onCancel}
      centered
      destroyOnHidden
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
      ]}
    >
      <Table
        columns={columns}
        dataSource={transmissions}
        loading={pending}
        pagination={{
          current: conditions.page,
          pageSize: conditions.perPage,
          onChange: handlePageChange,
          total: transmissionsCount,
        }}
        rowKey="id"
      />
    </Modal>
  )
}

TransmissionsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  patientId: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
}
